import { useRef, useState } from "react";
import { X, Plus, Upload, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, imgUrl, formatErr } from "@/lib/api";

export const Field = ({ label, helper, children }) => (
  <div>
    <label className="label">{label}</label>
    {children}
    {helper && <p className="helper">{helper}</p>}
  </div>
);

export function ChipInput({ value, onChange, placeholder, disabled, testid }) {
  const [draft, setDraft] = useState("");
  const add = () => {
    const v = draft.trim();
    if (v) onChange([...value, v]);
    setDraft("");
  };
  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-3" data-testid={`${testid}-chips`}>
        {value.map((c, i) => (
          <span key={i} className="inline-flex items-center gap-2 pl-3 pr-2 py-1.5 rounded-full text-xs bg-[#F1E9FE] border border-[#9C4DDC]/40 text-[#9C4DDC]">
            {c}
            {!disabled && <button type="button" onClick={() => onChange(value.filter((_, j) => j !== i))} data-testid={`${testid}-remove-${i}`}><X size={12} /></button>}
          </span>
        ))}
      </div>
      {!disabled && (
        <div className="flex gap-2">
          <input className="field" value={draft} placeholder={placeholder} onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} data-testid={`${testid}-input`} />
          <button type="button" className="btn-secondary px-4" onClick={add} data-testid={`${testid}-add-button`}><Plus size={16} /></button>
        </div>
      )}
    </div>
  );
}

export function ImageUpload({ value, onChange, disabled, testid, compact }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd);
      onChange(data.url);
    } catch (err) { toast.error(formatErr(err)); }
    finally { setBusy(false); e.target.value = ""; }
  };
  const src = imgUrl(value);
  return (
    <div className="flex items-center gap-4">
      <div className={`${compact ? "w-16 h-16" : "w-24 h-28"} rounded-[16px] overflow-hidden bg-[#FAF7FE] border border-[#F0EAF7] shrink-0 flex items-center justify-center`}>
        {src ? <img src={src} alt="" className="w-full h-full object-cover" data-testid={`${testid}-preview`} /> : <Upload size={18} className="text-[#CDBFE0]" />}
      </div>
      {!disabled && (
        <div className="flex flex-col gap-2 items-start">
          <input ref={ref} type="file" accept="image/*" hidden onChange={pick} data-testid={`${testid}-file-input`} />
          <button type="button" className="btn-secondary py-2 px-4" onClick={() => ref.current.click()} disabled={busy} data-testid={`${testid}-upload-button`}>
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} {value ? "Replace" : "Upload"}
          </button>
          {value && <button type="button" className="text-xs muted hover:text-red-400" onClick={() => onChange(null)} data-testid={`${testid}-remove-button`}>Remove</button>}
        </div>
      )}
    </div>
  );
}

export function ListEditor({ items, onChange, empty, fields, disabled, testid, addLabel }) {
  const update = (i, k, v) => onChange(items.map((it, j) => (j === i ? { ...it, [k]: v } : it)));
  return (
    <div className="space-y-4">
      {items.map((it, i) => (
        <div key={i} className="panel p-4 space-y-3 relative" data-testid={`${testid}-item-${i}`}>
          {!disabled && (
            <button type="button" className="absolute top-3 right-3 muted hover:text-red-400" onClick={() => onChange(items.filter((_, j) => j !== i))} data-testid={`${testid}-remove-${i}`}>
              <X size={16} />
            </button>
          )}
          {fields.map((f) => (
            <div key={f.key} className={f.key === fields[0].key ? "pr-8" : ""}>
              <label className="label">{f.label}</label>
              {f.render ? f.render(it, (v) => update(i, f.key, v), i) : f.multiline ? (
                <textarea className="field min-h-[90px]" disabled={disabled} value={it[f.key] || ""} placeholder={f.placeholder}
                  onChange={(e) => update(i, f.key, f.transform ? f.transform(e.target.value) : e.target.value)} data-testid={`${testid}-${f.key}-${i}`} />
              ) : (
                <input className="field" disabled={disabled} value={it[f.key] || ""} placeholder={f.placeholder}
                  onChange={(e) => update(i, f.key, f.transform ? f.transform(e.target.value) : e.target.value)} data-testid={`${testid}-${f.key}-${i}`} />
              )}
            </div>
          ))}
        </div>
      ))}
      {!disabled && (
        <button type="button" className="btn-secondary w-full" onClick={() => onChange([...items, { ...empty }])} data-testid={`${testid}-add-button`}>
          <Plus size={14} /> {addLabel}
        </button>
      )}
    </div>
  );
}

export function PillSelect({ options, value, onChange, multi, disabled, testid }) {
  const isOn = (o) => (multi ? value.includes(o) : value === o);
  const toggle = (o) => {
    if (disabled) return;
    if (multi) onChange(isOn(o) ? value.filter((x) => x !== o) : [...value, o]);
    else onChange(o);
  };
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => (
        <button key={o} type="button" onClick={() => toggle(o)} disabled={disabled}
          className={`px-4 py-2 rounded-full text-xs border transition-colors ${isOn(o) ? "bg-[#9C4DDC] text-white border-[#9C4DDC]" : "border-[#F0EAF7] text-[#6B6478] hover:border-[#9C4DDC]/60"}`}
          data-testid={`${testid}-option-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
          {o}
        </button>
      ))}
    </div>
  );
}
